import { forwardRef, useEffect, useRef, useState } from "react";
import api, { formatError } from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, FileText, Loader2, HeartPulse, IndianRupee, CheckCircle2, Clock } from "lucide-react";

const inr = (n) => `\u20B9${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const shortId = (s) => (s ? String(s).slice(0, 8).toUpperCase() : "—");

/**
 * SettlementStatements — hospital admin view of their daily payout ledger with
 * a one-click branded PDF download per row. PDF is generated client-side using
 * html2canvas (already a dep) — no new backend endpoint required.
 */
export default function SettlementStatements({ orgName }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // active payout for preview modal
  const [downloading, setDownloading] = useState(false);
  const sheetRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/admin/payouts?limit=100");
      setRows(data.payouts || []);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const download = async () => {
    if (!sheetRef.current) return;
    setDownloading(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(sheetRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement("a");
      link.download = `upchaar-settlement-${preview?.date || "statement"}-${shortId(preview?.id)}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Settlement statement downloaded");
    } catch (e) {
      toast.error(e.message || "Could not download");
    } finally {
      setDownloading(false);
    }
  };

  const badge = (s) => {
    if (s === "dispatched") return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1"><CheckCircle2 className="w-3 h-3" /> Dispatched</Badge>;
    if (s === "queued") return <Badge className="bg-amber-100 text-amber-800 border-amber-200 gap-1"><Clock className="w-3 h-3" /> Queued</Badge>;
    if (s === "failed") return <Badge className="bg-rose-100 text-rose-800 border-rose-200">Failed</Badge>;
    return <Badge variant="outline">{s || "—"}</Badge>;
  };

  return (
    <section data-testid="settlement-statements-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-2xl font-semibold text-slate-900">Settlement Statements</h2>
          <p className="text-sm text-slate-500">Daily payout ledger · download a branded PDF for any settlement</p>
        </div>
      </div>

      <Card className="medical-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Gross Earnings</TableHead>
              <TableHead className="text-right">Commission</TableHead>
              <TableHead className="text-right">GST</TableHead>
              <TableHead className="text-right">Net Payout</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Ref</TableHead>
              <TableHead className="text-right">Statement</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-10"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-12" data-testid="settlement-empty">No settlements yet. Payouts dispatch automatically @ 8:00 PM IST.</TableCell></TableRow>
            ) : rows.map((p) => (
              <TableRow key={p.id} data-testid={`settlement-row-${p.id}`}>
                <TableCell className="font-medium">{p.date}</TableCell>
                <TableCell className="text-right">{inr(p.gross)}</TableCell>
                <TableCell className="text-right text-slate-500">−{inr(p.commission)}</TableCell>
                <TableCell className="text-right text-slate-500">−{inr(p.gst)}</TableCell>
                <TableCell className="text-right font-semibold text-emerald-700">{inr(p.net)}</TableCell>
                <TableCell>{badge(p.status)}</TableCell>
                <TableCell className="font-mono text-xs text-slate-500">{shortId(p.id)}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => setPreview(p)}
                    className="gap-1.5 border-slate-300"
                    data-testid={`settlement-download-${p.id}`}>
                    <FileText className="w-3.5 h-3.5" /> View / Download
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-2xl bg-white p-0 overflow-hidden" data-testid="settlement-preview-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100 flex-row items-center justify-between">
            <DialogTitle className="text-slate-900">Settlement Statement</DialogTitle>
            <Button onClick={download} disabled={downloading} size="sm"
              className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5"
              data-testid="settlement-pdf-download-btn">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-3.5 h-3.5" /> Download PDF</>}
            </Button>
          </DialogHeader>
          {preview && (
            <div className="max-h-[70vh] overflow-y-auto bg-slate-100 p-4">
              <StatementSheet ref={sheetRef} payout={preview} orgName={orgName} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

// eslint-disable-next-line react/display-name
const StatementSheet = forwardRef(function StatementSheet({ payout: p, orgName }, ref) {
    const bank = p.bank_snapshot || {};
    return (
      <div ref={ref} className="bg-white shadow-lg mx-auto max-w-[720px]" data-testid="settlement-sheet">
        {/* Branded header — UpchaarQ teal/emerald palette */}
        <div className="bg-gradient-to-br from-sky-500 to-emerald-500 text-white px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 border border-white/30 flex items-center justify-center backdrop-blur">
              <HeartPulse className="w-6 h-6" />
            </div>
            <div>
              <div className="font-heading text-xl font-semibold tracking-tight">UpchaarQ</div>
              <div className="text-[11px] opacity-90">Trusted healthcare · Settlement statement</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] opacity-90 uppercase tracking-wider">Statement ID</div>
            <div className="font-mono text-sm font-semibold">{shortId(p.id)}</div>
          </div>
        </div>

        {/* Payee + date */}
        <div className="px-8 py-5 border-b border-slate-100 grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Payee</div>
            <div className="font-semibold text-slate-900">{orgName || "Hospital"}</div>
            {bank.account_holder && <div className="text-slate-600 text-xs mt-0.5">{bank.account_holder}</div>}
            {bank.bank_name && <div className="text-slate-500 text-xs">{bank.bank_name} · {bank.account_number_masked || ""}</div>}
            {bank.ifsc && <div className="text-slate-500 text-xs font-mono">IFSC {bank.ifsc}</div>}
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Settlement Date</div>
            <div className="font-semibold text-slate-900">{p.date}</div>
            <div className="text-xs text-slate-500 mt-0.5">Status: <span className="capitalize">{p.status}</span></div>
            {p.dispatched_at && (
              <div className="text-xs text-slate-500">Dispatched {new Date(p.dispatched_at).toLocaleString()}</div>
            )}
          </div>
        </div>

        {/* Numbers */}
        <div className="px-8 py-6">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-3">Earnings breakdown</div>
          <table className="w-full text-sm">
            <tbody>
              <Row label="Gross Earnings (patients × fees)" value={p.gross} />
              <Row label={`Platform Commission${p.platform_commission_pct ? ` (${p.platform_commission_pct}%)` : ""}`} value={p.commission} negative />
              <Row label="GST on Commission (18%)" value={p.gst} negative />
              <tr className="border-t-2 border-slate-200">
                <td className="py-3 text-sm font-heading font-semibold text-slate-900">Net Payout Dispatched</td>
                <td className="py-3 text-right font-heading font-bold text-emerald-700 text-lg">{inr(p.net)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Meta */}
        <div className="px-8 pb-5 grid grid-cols-2 gap-4 text-xs text-slate-600">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Reference</div>
            <div className="font-mono">{p.id}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Patients Settled</div>
            <div><IndianRupee className="w-3 h-3 inline -mt-0.5" /> {p.patient_count ?? "—"} appointments</div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-50 border-t border-slate-100 px-8 py-4 text-[11px] text-slate-500 flex items-center justify-between">
          <div>
            This is a system-generated statement. Contact <span className="text-slate-700 font-medium">support@upchaarq.in</span> for disputes within 7 days.
          </div>
          <div className="font-mono">upchaarq.in</div>
        </div>
      </div>
    );
});

function Row({ label, value, negative }) {
  return (
    <tr className="border-b border-slate-100">
      <td className="py-2.5 text-slate-700">{label}</td>
      <td className={`py-2.5 text-right ${negative ? "text-slate-600" : "text-slate-900 font-medium"}`}>
        {negative ? "−" : ""}{inr(value)}
      </td>
    </tr>
  );
}
